import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Grid,
    TextField,
    Typography,
    IconButton,
    Stack,
    Divider,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    OutlinedInput,
    Chip,
    Box,
    Autocomplete,
    CircularProgress,
    useMediaQuery,
    useTheme
} from '@mui/material';
import { Close as CloseIcon, FilterList as FilterIcon } from '@mui/icons-material';
import { ALERT_TYPES } from '../SourceSuspiciousTicksList';
import { getInstrumentList } from '../../../../redux/slices/trades';

// Helper functions for datetime formatting
const formatDateTimeForInput = (date) => {
    if (!date) return '';
    const d = new Date(date);
    const year = d.getFullYear();
    const month = (d.getMonth() + 1).toString().padStart(2, '0');
    const day = d.getDate().toString().padStart(2, '0');
    const hours = d.getHours().toString().padStart(2, '0');
    const minutes = d.getMinutes().toString().padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
};

const SuspiciousTicksFiltersModal = ({ open, onClose, onApply, currentFilters }) => {
    const dispatch = useDispatch();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    // Redux state with proper error handling
    const tradesState = useSelector((state) => state.trade || {});
    const { instrumentData = {}, isLoading = false } = tradesState;

    // Helper function to convert instrumentData to array
    const getInstrumentsArray = (data) => {
        if (data && data.docs && Array.isArray(data.docs)) {
            return data.docs;
        }
        if (Array.isArray(data)) {
            return data;
        }
        if (data && typeof data === 'object') {
            return Object.values(data);
        }
        return [];
    };

    // Convert instrumentData to array - use docs property
    const instrumentsArray = getInstrumentsArray(instrumentData);

    // Filter MCX-FUT instruments only
    const mcxFutInstruments = instrumentsArray?.filter(instrument => {
        // Check various possible segment/exchange formats
        const segment = instrument.segment;
        const exchangeName = instrument.exchange?.name;
        const instrumentType = instrument.instrument_type;
        const tradingSymbol = instrument.tradingsymbol || instrument.tradingSymbol || instrument.name;

        // MCX-FUT filtering logic - looking for MCX exchange with FUT instrument type
        return (
            exchangeName === 'MCX' && instrumentType === 'FUT'
        );
    }) || [];

    if (instrumentsArray.length > 0) {
        const exchangeTypes = [...new Set(instrumentsArray.map(inst => inst.exchange?.name))];
        // Log different segments
        const segments = [...new Set(instrumentsArray.map(inst => inst.segment))];
    }

    // Form state
    const [filters, setFilters] = useState({
        instrumentTokens: [],
        alertTypes: [],
        from: '',
        to: '',
        startPrice: '',
        endPrice: '',
    });

    // Load instruments on modal open
    useEffect(() => {
        if (open && instrumentsArray.length === 0) {
            dispatch(getInstrumentList())
                .then((result) => {
                })
                .catch((error) => {
                });
        }
    }, [open, dispatch, instrumentsArray.length]);

    // Load current filters when modal opens
    useEffect(() => {
        if (open && currentFilters) {
            setFilters({
                instrumentTokens: currentFilters.instrumentTokens ? currentFilters.instrumentTokens.split(',') : [],
                alertTypes: currentFilters.alertTypes ? currentFilters.alertTypes.split(',') : [],
                from: currentFilters.from ? formatDateTimeForInput(currentFilters.from) : '',
                to: currentFilters.to ? formatDateTimeForInput(currentFilters.to) : '',
                startPrice: currentFilters.startPrice || '',
                endPrice: currentFilters.endPrice || '',
            });
        }
    }, [open, currentFilters]);

    // Handle form changes
    const handleInputChange = (field) => (event) => {
        const value = event.target.value;
        setFilters(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleMultiSelectChange = (field) => (event) => {
        const value = typeof event.target.value === 'string' ? event.target.value.split(',') : event.target.value;
        setFilters(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleInstrumentChange = (event, newValue) => {
        setFilters(prev => ({
            ...prev,
            instrumentTokens: newValue.map(instrument =>
                instrument.instrument_token || instrument._id
            )
        }));
    };

    // Handle apply filters
    const handleApply = () => {
        const appliedFilters = {};

        if (filters.instrumentTokens.length > 0) {
            appliedFilters.instrumentTokens = filters.instrumentTokens.join(',');
        }

        if (filters.alertTypes.length > 0) {
            appliedFilters.alertTypes = filters.alertTypes.join(',');
        }

        if (filters.from) {
            appliedFilters.from = new Date(filters.from).toISOString();
        }

        if (filters.to) {
            appliedFilters.to = new Date(filters.to).toISOString();
        }

        if (filters.startPrice) {
            appliedFilters.startPrice = parseFloat(filters.startPrice);
        }

        if (filters.endPrice) {
            appliedFilters.endPrice = parseFloat(filters.endPrice);
        }

        onApply(appliedFilters);
    };

    // Handle reset
    const handleReset = () => {
        setFilters({
            instrumentTokens: [],
            alertTypes: [],
            from: '',
            to: '',
            startPrice: '',
            endPrice: '',
        });
    };

    const getSelectedInstruments = () => {
        return mcxFutInstruments.filter(instrument =>
            filters.instrumentTokens.includes(
                instrument.instrument_token || instrument._id
            )
        );
    };

    // Helper function to get no options text
    const getNoOptionsText = () => {
        if (isLoading) {
            return "Loading instruments...";
        }
        if (mcxFutInstruments.length === 0) {
            return "No MCX-FUT instruments available";
        }
        return "No matching instruments";
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth={isMobile ? "xs" : "sm"}
            fullWidth
            fullScreen={isMobile}
            PaperProps={{
                sx: {
                    minHeight: isMobile ? '100vh' : '70vh',
                    margin: isMobile ? 0 : 2,
                    borderRadius: isMobile ? 0 : 2
                }
            }}
        >
            <DialogTitle
                sx={{
                    pb: 1,
                    backgroundColor: theme.palette.primary.main,
                    color: 'white',
                    fontSize: '1rem'
                }}
            >
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <FilterIcon fontSize="small" />
                        <Typography variant="h6" fontSize="1rem">
                            Filters
                        </Typography>
                    </Stack>
                    <IconButton
                        onClick={onClose}
                        size="small"
                        sx={{ color: 'white' }}
                    >
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </Stack>
            </DialogTitle>

            <DialogContent
                dividers
                sx={{
                    px: isMobile ? 2 : 3,
                    py: 2,
                    '& .MuiTextField-root': {
                        '& .MuiInputLabel-root': { fontSize: '0.875rem' },
                        '& .MuiInputBase-input': { fontSize: '0.875rem' }
                    }
                }}
            >
                <Grid container spacing={isMobile ? 2 : 3}>
                    {/* Date Range - Compact */}
                    <Grid item xs={12}>
                        <Typography variant="subtitle2" gutterBottom color="primary" fontWeight="600">
                            Date & Time Range
                        </Typography>
                    </Grid>
                    <Grid item xs={6}>
                        <TextField
                            fullWidth
                            label="From"
                            type="datetime-local"
                            size="small"
                            value={filters.from}
                            onChange={handleInputChange('from')}
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <TextField
                            fullWidth
                            label="To"
                            type="datetime-local"
                            size="small"
                            value={filters.to}
                            onChange={handleInputChange('to')}
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>

                    {/* Alert Types - Compact */}
                    <Grid item xs={12} sx={{ mt: 1 }}>
                        <Typography variant="subtitle2" gutterBottom color="primary" fontWeight="600">
                            Alert Types
                        </Typography>
                        <FormControl fullWidth size="small">
                            <Select
                                multiple
                                value={filters.alertTypes}
                                onChange={handleMultiSelectChange('alertTypes')}
                                displayEmpty
                                renderValue={(selected) =>
                                    selected.length === 0 ? (
                                        <Typography variant="body2" color="text.secondary">
                                            Select alert types...
                                        </Typography>
                                    ) : (
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                            {selected.slice(0, 2).map((value) => (
                                                <Chip
                                                    key={value}
                                                    label={value.replace(/_/g, ' ')}
                                                    size="small"
                                                    variant="outlined"
                                                />
                                            ))}
                                            {selected.length > 2 && (
                                                <Chip
                                                    label={`+${selected.length - 2} more`}
                                                    size="small"
                                                    variant="outlined"
                                                    color="primary"
                                                />
                                            )}
                                        </Box>
                                    )
                                }
                                MenuProps={{
                                    PaperProps: {
                                        style: {
                                            maxHeight: 250,
                                            width: 280,
                                        },
                                    },
                                }}
                            >
                                {ALERT_TYPES.map((alertType) => (
                                    <MenuItem key={alertType.type} value={alertType.type}>
                                        <Box sx={{ py: 0.5 }}>
                                            <Typography variant="body2" fontWeight="medium" fontSize="0.875rem">
                                                {alertType.type.replace(/_/g, ' ')}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary" fontSize="0.75rem">
                                                {alertType.description}
                                            </Typography>
                                        </Box>
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>

                    {/* MCX-FUT Instruments - Dynamic */}
                    <Grid item xs={12} sx={{ mt: 1 }}>
                        <Typography variant="subtitle2" gutterBottom color="primary" fontWeight="600">
                            MCX-FUT Instruments {isLoading && <CircularProgress size={16} sx={{ ml: 1 }} />}
                        </Typography>
                        <Autocomplete
                            multiple
                            size="small"
                            options={mcxFutInstruments}
                            getOptionLabel={(option) => {
                                const name = option.tradingsymbol || option.name;
                                const segment = option.segment;
                                const exchangeName = option.exchange?.name;
                                return `${name} (${exchangeName}-${segment})`;
                            }}
                            value={getSelectedInstruments()}
                            onChange={handleInstrumentChange}
                            loading={isLoading}
                            noOptionsText={getNoOptionsText()}
                            renderTags={(value, getTagProps) =>
                                value.slice(0, 2).map((option, index) => (
                                    <Chip
                                        variant="outlined"
                                        label={option.tradingsymbol || option.name}
                                        size="small"
                                        {...getTagProps({ index })}
                                        key={option.instrument_token || option._id}
                                    />
                                )).concat(
                                    value.length > 2 ? [
                                        <Chip
                                            key="more"
                                            label={`+${value.length - 2} more`}
                                            size="small"
                                            variant="outlined"
                                            color="primary"
                                        />
                                    ] : []
                                )
                            }
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    placeholder="Search MCX-FUT instruments..."
                                    InputProps={{
                                        ...params.InputProps,
                                        endAdornment: (
                                            <>
                                                {isLoading ? <CircularProgress color="inherit" size={20} /> : null}
                                                {params.InputProps.endAdornment}
                                            </>
                                        ),
                                    }}
                                />
                            )}
                            renderOption={(props, option) => (
                                <Box component="li" {...props} key={option.instrument_token || option._id}>
                                    <Box sx={{ py: 0.5 }}>
                                        <Typography variant="body2" fontSize="0.875rem">
                                            {option.tradingsymbol || option.name}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary" fontSize="0.75rem">
                                            {option.exchange?.name}-{option.segment} • Token: {option.instrument_token || 'N/A'}
                                        </Typography>
                                    </Box>
                                </Box>
                            )}
                        />
                    </Grid>

                    {/* Price Range - Compact */}
                    <Grid item xs={12} sx={{ mt: 1 }}>
                        <Typography variant="subtitle2" gutterBottom color="primary" fontWeight="600">
                            Price Range
                        </Typography>
                    </Grid>
                    <Grid item xs={6}>
                        <TextField
                            fullWidth
                            label="Min Price"
                            type="number"
                            size="small"
                            value={filters.startPrice}
                            onChange={handleInputChange('startPrice')}
                            inputProps={{ min: 0, step: 0.01 }}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <TextField
                            fullWidth
                            label="Max Price"
                            type="number"
                            size="small"
                            value={filters.endPrice}
                            onChange={handleInputChange('endPrice')}
                            inputProps={{ min: 0, step: 0.01 }}
                        />
                    </Grid>
                </Grid>
            </DialogContent>

            <DialogActions
                sx={{
                    px: isMobile ? 2 : 3,
                    py: 1.5,
                    backgroundColor: theme.palette.grey[50]
                }}
            >
                <Stack
                    direction="row"
                    spacing={1}
                    sx={{ width: '100%', justifyContent: 'space-between' }}
                >
                    <Button
                        variant="outlined"
                        color="secondary"
                        size="small"
                        onClick={handleReset}
                        sx={{ fontSize: '0.875rem' }}
                    >
                        Reset
                    </Button>
                    <Stack direction="row" spacing={1}>
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={onClose}
                            sx={{ fontSize: '0.875rem' }}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            size="small"
                            onClick={handleApply}
                            sx={{ fontSize: '0.875rem' }}
                        >
                            Apply
                        </Button>
                    </Stack>
                </Stack>
            </DialogActions>
        </Dialog>
    );
};

SuspiciousTicksFiltersModal.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onApply: PropTypes.func.isRequired,
    currentFilters: PropTypes.object,
};

export default SuspiciousTicksFiltersModal;
